import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OpenSeadragon, { Point } from "openseadragon";
import "./ExplorationMode.css";
import CustomButton from "../Buttons/CustomButton.js";
import SliderText from "../Slidertext/Slidertext.js";
import TimeoutComponent from "../TimeoutComponent/TimeoutComponent";
import preventClick from "../../utils/PreventClick";

// Icon
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";

const ExplorationMode = () => {
	const [items, setItems] = useState([]);
	const [selectedItemIndex, setSelectedItemIndex] = useState(0);
	const [viewer, setViewer] = useState(null);
	const [showMarkers, setShowMarkers] = useState(true);
	const NAVIGATE = useNavigate();
	const LOCATION = useLocation();
	const [image_width, setImageWidth] = useState(0);

	// Fonction pour calculer le centre et le zoom pour une annotation spécifique
	const calculateCenterAndZoom = (X, Y, W, H, viewer) => {
		const centerX = (X + W / 2) / image_width;
		const centerY = (Y + H / 2) / image_width;
		const viewerWidth = viewer.container.clientWidth;
		const viewerHeight = viewer.container.clientHeight;
		const zoomWidth = viewerWidth / image_width / (W * 1.5 / image_width);
		const zoomHeight = viewerHeight / image_width / (H * 1.5 / image_width);
		const zoomFocal = 7;
		const calculatedZoom = Math.max(zoomWidth, zoomHeight) * zoomFocal;
		return { centerX, centerY, calculatedZoom };
	};

	TimeoutComponent(NAVIGATE);

	preventClick();


	// useEffect pour charger les données et initialiser OpenSeadragon
	useEffect(() => {
		const fetchData = async () => {
			try {
				// Récupération des données du fichier JSON
				const responseJsonFile = await fetch(LOCATION.state);
				const dataJsonFile = await responseJsonFile.json();

				if (dataJsonFile?.source) {
					// Récupération des données IIIF
					const responseIIIF = await fetch(dataJsonFile.source);
					const dataIIIF = await responseIIIF.json();

					if (dataIIIF?.width) {
						setImageWidth(dataIIIF.width);
					}



					// Initialisation d'OpenSeadragon avec l'URL obtenue du JSON
					const OSD_VIEWER = OpenSeadragon({
						id: "openseadragon-viewer",
						tileSources: dataJsonFile.source,
						showZoomControl: false,
						showHomeControl: false,
						showFullPageControl: false,

					});

					setViewer(OSD_VIEWER);

					if (dataJsonFile?.first?.items) {
						setItems(dataJsonFile.first.items);
						addClickHandler(OSD_VIEWER, dataJsonFile.first.items);
					}
				}
			} catch (error) {
				console.error("Error fetching JSON:", error);
			}
		};

		fetchData();
	}, [LOCATION.state]);




	// Fonction pour ajouter un gestionnaire de clic sur le canvas d'OpenSeadragon
	const addClickHandler = (OSD_VIEWER, items) => {
		OSD_VIEWER.addHandler("canvas-click", (event) => {
			const CLICKED_ELEMENT = event.originalTarget;
			if (CLICKED_ELEMENT.classList.contains("white-annotation") || CLICKED_ELEMENT.classList.contains("svg-annotation")) {
				OSD_VIEWER.gestureSettingsMouse.clickToZoom = false;
				handleImageElementClick(CLICKED_ELEMENT.id, items);
			}
		});
	};

	// Fonction pour ajouter des overlays (annotations) sur l'image dans OpenSeadragon
	const addOverlays = (OSD_VIEWER, items) => {

		items.forEach((item, index) => {
			const { value } = item.target.selector;
			const MATCH = value.match(/xywh=pixel:([0-9.]+),([0-9.]+),([0-9.]+),([0-9.]+)/);
			if (MATCH) {
				const [X, Y, W, H] = MATCH.slice(1).map(Number);
				const { centerX, centerY } = calculateCenterAndZoom(X, Y, W, H, OSD_VIEWER);
				const WHITE_IMAGE_ELEMENT = document.createElement("div");
				WHITE_IMAGE_ELEMENT.className = "white-annotation";
				WHITE_IMAGE_ELEMENT.id = index + 1;

				const CIRCLE_IMAGE_ELEMENT = document.createElement("div");
				CIRCLE_IMAGE_ELEMENT.className = "circle-annotation";
				CIRCLE_IMAGE_ELEMENT.id = index + 1;

				const svgElement = document.createElement("img");
				svgElement.setAttribute("src", "Assets/icon-eye-white.svg");
				svgElement.className = "svg-annotation";
				svgElement.id = index + 1;


				WHITE_IMAGE_ELEMENT.appendChild(CIRCLE_IMAGE_ELEMENT);
				CIRCLE_IMAGE_ELEMENT.appendChild(svgElement);

				OSD_VIEWER.addOverlay({
					element: WHITE_IMAGE_ELEMENT,
					location: new Point(centerX, centerY),
				});

				// Utilisez document pour sélectionner les éléments .svg-annotation à l'intérieur de WHITE_IMAGE_ELEMENT
				const svgElements = WHITE_IMAGE_ELEMENT.querySelectorAll(".svg-annotation");

				// Parcourir ces éléments et leur attribuer un ID basé sur leur index
				svgElements.forEach((svgElementID) => {
					svgElementID.id = index + 1;
				});
			}
			index++;
		});
	};

	// Fonction pour gérer le clic sur une annotation
	const handleImageElementClick = (index, items) => {
		setSelectedItemIndex(index - 1);
	};

	const handleRecenter = (index, items, OSD_VIEWER) => {
		const selectedItem = items[index - 1];
		const { value } = selectedItem.target.selector;
		const MATCH = value.match(/xywh=pixel:([0-9.]+),([0-9.]+),([0-9.]+),([0-9.]+)/);
		if (MATCH) {
			const [X, Y, W, H] = MATCH.slice(1).map(Number);
			const { centerX, centerY, calculatedZoom } = calculateCenterAndZoom(X, Y, W, H, OSD_VIEWER);
			OSD_VIEWER.viewport.panTo(new OpenSeadragon.Point(centerX, centerY));
			OSD_VIEWER.viewport.zoomTo(calculatedZoom);
		}
	};

	// useEffect pour gérer le pan et le zoom vers l'annotation sélectionnée
	useEffect(() => {
		if (viewer && items.length > 0) {
			const selectedItem = items[selectedItemIndex];
			const { value } = selectedItem.target.selector;
			const MATCH = value.match(/xywh=pixel:([0-9.]+),([0-9.]+),([0-9.]+),([0-9.]+)/);
			if (MATCH) {
				const [X, Y, W, H] = MATCH.slice(1).map(Number);
				const { centerX, centerY, calculatedZoom } = calculateCenterAndZoom(X, Y, W, H, viewer);
				viewer.viewport.panTo(new OpenSeadragon.Point(centerX, centerY));
				viewer.viewport.zoomTo(calculatedZoom);
			}
		}
	}, [viewer, items, selectedItemIndex]);

	// useEffect pour afficher/masquer les annotations
	useEffect(() => {
		if (showMarkers) {
			addOverlays(viewer, items);
			updateMarkerColors();
		} else {
			items.forEach((_, index) => viewer.removeOverlay((index + 1).toString()));
		}
	}, [showMarkers, items, viewer]);

	// useEffect pour mettre à jour les couleurs des marqueurs
	useEffect(() => {
		updateMarkerColors();
	}, [selectedItemIndex, items]);

	// Fonction pour mettre à jour les couleurs des marqueurs
	const updateMarkerColors = () => {
		items.forEach((item, index) => {
			const markerElement = document.getElementById(index + 1);
			if (markerElement) {
				const svgAnnotation = markerElement.querySelector(".svg-annotation");
				if (index === selectedItemIndex) {
					markerElement.classList.remove("white-annotation");
					markerElement.classList.add("selected-annotation");
					markerElement.querySelector(".circle-annotation").style.borderColor = "white";
					if (svgAnnotation) svgAnnotation.setAttribute("src", "Assets/icon-eye-black.svg");

				} else {
					markerElement.classList.remove("selected-annotation");
					markerElement.classList.add("white-annotation");
					markerElement.querySelector(".circle-annotation").style.borderColor = "black";
					if (svgAnnotation) svgAnnotation.setAttribute("src", "Assets/icon-eye-white.svg");

				}
			}
		});
	};

	// Fonction pour basculer l'affichage des marqueurs
	const toggleShowMarkers = () => {
		setShowMarkers(!showMarkers);
	};

	return (
		// Structure du composant avec la partie gauche pour les annotations et la droite pour le viewer
		// Boutons flottants pour les actions de l'utilisateur
		<div className="container-exploration">

			<div className="left-part-exploration">
				<div className="annotation-container-exploration" style={{ flex: "1" }}>
					<SliderText className="slider-text"
						htmlContent={
							items[selectedItemIndex]?.body.find(part => part.type === "HTMLBody")?.value
						}
					/>

				</div>
				<div className={"home-section-exploration"}>
					<CustomButton
						isDisabled={false}
						icon={<HomeOutlinedIcon style={{ fontSize: 40 }} />}
						actionToDo={() => {
							NAVIGATE("/");
						}}
					/>
				</div>

			</div>
			<div className="right-part-exploration">
				<div id="openseadragon-viewer" style={{ width: "100%", height: "100vh" }}></div>
				<div className="floating-button-exploration">
					<CustomButton
						isDisabled={false}
						actionToDo={() => handleRecenter(selectedItemIndex + 1, items, viewer)}
						icon={<CenterFocusWeakIcon style={{ fontSize: 40 }} />}
					/>
					<CustomButton
						isDisabled={false}
						actionToDo={toggleShowMarkers}
						icon={showMarkers ? <VisibilityIcon style={{ fontSize: 40 }} /> : <VisibilityOffIcon style={{ fontSize: 40 }} />}
					/>
				</div>

			</div>
		</div>
	);
};

export default ExplorationMode;
